// =============================================================================
// LINTER RULES — the house-style linter POND (cross-user, research-tuned).
//
// The regex rules the text gates fire: AI-tells (AuLinterService.AI_TELL_PATTERNS)
// and undersell/flattery tells (AntiSellShortService.SELL_SHORT_PATTERNS). ONE table
// with a `rule_class` discriminator since the two shapes are near-identical — Slice 3
// reuses this table outright. Rules are DATA (a regex source + flags + reason +
// optional user-facing suggestion), not a literal in the algorithm: they are
// research-updated over time (the 2026-06-19 "what-lands" pass) and apply to every
// user. The pure match logic (collectAiTells / lintSellShort) stays in the services
// and takes the compiled rules INJECTED.
//
// A POND behind LinterRuleSource (apps/api/services/lake/LinterRuleService.ts):
// Postgres today, swappable. Cross-user by construction (no userId), mirroring the
// platform_algorithm / industry_vocab lakes.
//
// GOLDEN RISK (the trickiest migration): the rules are LIVE RegExp — store the
// `pattern` SOURCE + `flags` separately and recompile `new RegExp(source, flags)`;
// load ORDER BY sort_order so collectAiTells emits matches in the same order. The
// golden test asserts the compiled-from-pond rules reproduce the gate output
// byte-for-byte over a fixture corpus.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool, mirrored
// here in Drizzle and exported from index.ts.
// =============================================================================

import { index, integer, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, lakeProvenance, timestamps } from './_shared';

export const linterRules = pgTable(
  'linter_rules',
  {
    id: id(),
    /** Which gate this rule belongs to — 'ai_tell' | 'sell_short'. */
    ruleClass: varchar('rule_class', { length: 16 }).notNull(),
    /** Stable per-class key for upsert (slug; unique within rule_class). */
    ruleKey: varchar('rule_key', { length: 120 }).notNull(),
    /** The RegExp SOURCE (e.g. '\\bdelve\\b'). Recompiled with `flags` at load. */
    pattern: text('pattern').notNull(),
    /** RegExp flags ('gi' | 'g' | 'gm'). */
    flags: varchar('flags', { length: 8 }).notNull().default('gi'),
    /** The reason slug emitted on a match (e.g. 'cliche:cutting_edge', 'hedge'). */
    reason: varchar('reason', { length: 64 }).notNull(),
    /** Optional grouping ('cliche'|'overused_word'|'punctuation'|'flattery'|'structure'|'opener'|…). */
    category: varchar('category', { length: 24 }),
    /** Where it fires — 'universal' (every English variant; house style) is the default.
     *  Locale-specific spelling lives in its OWN pond, never here. */
    scope: varchar('scope', { length: 16 }).notNull().default('universal'),
    /** User-facing stronger-framing copy (sell_short only; null for ai_tell). */
    suggestion: text('suggestion'),
    /** Why this rule exists / evidence (e.g. 'PNAS 2025 Reinhart'). */
    evidence: text('evidence'),
    /** 'high' | 'medium' | 'low' — evidence strength. */
    confidence: varchar('confidence', { length: 8 }),
    /** Match/emit order WITHIN a class — preserved so gate output stays byte-for-byte. */
    sortOrder: integer('sort_order').notNull().default(0),
    ...lakeProvenance,
    ...timestamps,
  },
  (t) => ({
    classIdx: index('linter_rules_class_idx').on(t.ruleClass, t.sortOrder),
    // UNIQUE (rule_class, rule_key) — one rule per key per class, so a re-seed upserts
    // in place. Apply via the Neon tool:
    //   CREATE UNIQUE INDEX IF NOT EXISTS linter_rules_key_uq
    //     ON linter_rules (rule_class, rule_key);
    keyUq: uniqueIndex('linter_rules_key_uq').on(t.ruleClass, t.ruleKey),
  }),
);
